import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, Copy, PlugZap, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ModeBadge = ({ mode, testId }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wide rounded px-2 py-0.5 ${mode === "live" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`} data-testid={testId}>{mode === "live" ? "LIVE" : mode === "test" ? "TEST" : "SANDBOX"}</span>
);

export const PayPalSettingsCard = () => {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ paypal_client_id: "", paypal_client_secret: "", paypal_webhook_id: "" });
  const [busy, setBusy] = useState(null);
  const load = () => axios.get(`${API}/settings/payments/paypal`).then((r) => { setS(r.data); setForm((f) => ({ ...f, paypal_client_id: r.data.paypal_client_id, paypal_webhook_id: r.data.paypal_webhook_id })); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const save = async (patch) => {
    setBusy("save");
    try {
      const { data } = await axios.put(`${API}/settings/payments/paypal`, patch);
      setS(data);
      setForm((f) => ({ ...f, paypal_client_secret: "" }));
      toast.success("PayPal settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save PayPal settings");
    } finally { setBusy(null); }
  };
  const test = async () => {
    setBusy("test");
    try {
      const { data } = await axios.post(`${API}/settings/payments/paypal/test`);
      toast.success(`PayPal connected (${data.mode})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PayPal connection failed");
    } finally { setBusy(null); }
  };
  const webhookUrl = `${window.location.origin}/api/paypal/webhook`;
  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="paypal-settings-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Wallet className="w-6 h-6 text-[#003087]" /> PayPal Integration <ModeBadge mode={s.paypal_mode} testId="paypal-mode-badge" /></CardTitle>
            <p className="text-sm text-slate-600 mt-2">Offer PayPal on the View &amp; Pay page and (optionally) save PayPal accounts for autopay (Vault).</p>
          </div>
          {s.paypal_configured && <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm" data-testid="paypal-connected-pill"><div className="w-2 h-2 bg-green-500 rounded-full" />Configured</div>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
          <div><p className="font-semibold text-slate-800">Offer PayPal to customers</p><p className="text-xs text-slate-500">Shows PayPal buttons on the public pay page.</p></div>
          <Switch checked={s.paypal_enabled} disabled={!s.paypal_configured} onCheckedChange={(v) => save({ paypal_enabled: v })} data-testid="paypal-enabled-toggle" />
        </div>
        <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
          <div><p className="font-semibold text-slate-800">Allow PayPal autopay</p><p className="text-xs text-slate-500">Customers on recurring invoices can save their PayPal account for automatic payments.</p></div>
          <Switch checked={s.paypal_autopay_enabled} disabled={!s.paypal_configured} onCheckedChange={(v) => save({ paypal_autopay_enabled: v })} data-testid="paypal-autopay-toggle" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Environment</Label>
            <Select value={s.paypal_mode} onValueChange={(v) => save({ paypal_mode: v })}>
              <SelectTrigger data-testid="paypal-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="sandbox">Sandbox (testing)</SelectItem><SelectItem value="live">Live</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="paypal_client_id">Client ID</Label>
            <Input id="paypal_client_id" value={form.paypal_client_id} onChange={(e) => setForm({ ...form, paypal_client_id: e.target.value })} className="font-mono text-sm" data-testid="paypal-client-id-input" />
          </div>
          <div>
            <Label htmlFor="paypal_client_secret">Client secret {s.paypal_client_secret_configured && <span className="text-xs text-slate-500">(saved · ends in {s.paypal_client_secret_last4}; leave blank to keep)</span>}</Label>
            <Input id="paypal_client_secret" type="password" value={form.paypal_client_secret} onChange={(e) => setForm({ ...form, paypal_client_secret: e.target.value })} placeholder={s.paypal_client_secret_configured ? "••••••••" : "Paste client secret"} className="font-mono text-sm" data-testid="paypal-client-secret-input" />
          </div>
          <div>
            <Label htmlFor="paypal_webhook_id">Webhook ID</Label>
            <Input id="paypal_webhook_id" value={form.paypal_webhook_id} onChange={(e) => setForm({ ...form, paypal_webhook_id: e.target.value })} placeholder="From PayPal developer dashboard" className="font-mono text-sm" data-testid="paypal-webhook-id-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div><Label>PayPal fee percent (%)</Label><Input type="number" step="0.1" min="0" max="20" value={s.paypal_fee_percent} onChange={(e) => setS({ ...s, paypal_fee_percent: e.target.value })} data-testid="paypal-fee-percent-input" /></div>
          <div><Label>Fixed fee ($)</Label><Input type="number" step="0.01" min="0" max="20" value={s.paypal_fee_fixed} onChange={(e) => setS({ ...s, paypal_fee_fixed: e.target.value })} data-testid="paypal-fee-fixed-input" /></div>
        </div>
        <p className="text-xs text-slate-500">Default 0 — PayPal payers pay the exact balance. Set a fee here if you ever need to pass processing costs on.</p>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
          <p className="font-semibold mb-1">Webhook URL (add in PayPal → Apps &amp; Credentials → Webhooks)</p>
          <div className="flex items-center gap-2"><code className="bg-white border rounded px-2 py-1 flex-1 break-all" data-testid="paypal-webhook-url">{webhookUrl}</code>
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(webhookUrl).then(() => toast.success("Copied"))} data-testid="paypal-webhook-copy"><Copy className="w-3 h-3" /></Button></div>
          <p className="mt-1">Events: PAYMENT.CAPTURE.COMPLETED, PAYMENT.CAPTURE.DENIED, PAYMENT.CAPTURE.DECLINED, CHECKOUT.ORDER.APPROVED, VAULT.PAYMENT-TOKEN.CREATED, VAULT.PAYMENT-TOKEN.DELETED</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => save({ paypal_client_id: form.paypal_client_id, paypal_client_secret: form.paypal_client_secret || undefined, paypal_webhook_id: form.paypal_webhook_id, paypal_fee_percent: parseFloat(s.paypal_fee_percent) || 0, paypal_fee_fixed: parseFloat(s.paypal_fee_fixed) || 0 })} disabled={!!busy} className="bg-[#003087] hover:bg-[#001f5c]" data-testid="paypal-save-button"><CheckCircle2 className="w-4 h-4 mr-2" />{busy === "save" ? "Saving…" : "Save PayPal settings"}</Button>
          <Button variant="outline" onClick={test} disabled={!!busy || !s.paypal_configured} data-testid="paypal-test-button"><PlugZap className="w-4 h-4 mr-2" />{busy === "test" ? "Testing…" : "Test connection"}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const AutopaySettingsSection = () => {
  const [s, setS] = useState(null);
  const [consent, setConsent] = useState("");
  const [busy, setBusy] = useState(null);
  const load = () => axios.get(`${API}/settings/payments/stripe`).then((r) => { setS(r.data); setConsent(r.data.autopay_consent_text); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!s) return null;
  const save = async (patch) => {
    setBusy("save");
    try {
      const { data } = await axios.put(`${API}/settings/payments/stripe`, patch);
      setS(data);
      setConsent(data.autopay_consent_text);
      toast.success("Autopay settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save autopay settings");
    } finally { setBusy(null); }
  };
  const testStripe = async () => {
    setBusy("test");
    try {
      const { data } = await axios.post(`${API}/settings/payments/stripe/test`);
      toast.success(`Stripe connected (${data.mode}) — ${data.account_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe connection failed");
    } finally { setBusy(null); }
  };
  return (
    <div className="rounded-xl border-2 border-indigo-300 p-5 space-y-4" data-testid="autopay-settings-section">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">Autopay for recurring invoices {s.stripe.configured && <ModeBadge mode={s.stripe.mode} testId="stripe-mode-badge" />}</h3>
        <Button size="sm" variant="outline" onClick={testStripe} disabled={!!busy || !s.stripe.configured} data-testid="stripe-test-button"><PlugZap className="w-4 h-4 mr-1" />{busy === "test" ? "Testing…" : "Test Stripe"}</Button>
      </div>
      <p className="text-xs text-slate-500">Customers paying a recurring invoice can consent to saving their card / PayPal account; future invoices in the series are then charged automatically. Hosted Stripe Checkout and PayPal only — no card numbers touch this app.</p>
      <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
        <div><p className="font-semibold text-slate-800 text-sm">Offer autopay</p><p className="text-xs text-slate-500">Shows the consent checkbox on recurring invoices and runs scheduled charges.</p></div>
        <Switch checked={s.autopay_enabled} onCheckedChange={(v) => save({ autopay_enabled: v })} data-testid="autopay-enabled-toggle" />
      </div>
      <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
        <div><p className="font-semibold text-slate-800 text-sm">Auto-issue recurring invoices</p><p className="text-xs text-slate-500">Generate and email the next invoice in each active series on its issue date (off = reminder emails only, as today).</p></div>
        <Switch checked={s.recurring_auto_issue_enabled} onCheckedChange={(v) => save({ recurring_auto_issue_enabled: v })} data-testid="recurring-auto-issue-toggle" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label>Charge timing</Label>
          <Select value={s.autopay_charge_timing} onValueChange={(v) => save({ autopay_charge_timing: v })}>
            <SelectTrigger data-testid="autopay-timing-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="on_due_date">On the due date</SelectItem><SelectItem value="on_issue">When the invoice is issued</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label>Retry after failed charge (days)</Label>
          <Input value={s.autopay_retry_days.join(", ")} onChange={(e) => setS({ ...s, autopay_retry_days: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} onBlur={() => save({ autopay_retry_days: s.autopay_retry_days })} data-testid="autopay-retry-input" />
        </div>
        <div>
          <Label>Max attempts</Label>
          <Input type="number" min="1" max="6" value={s.autopay_max_attempts} onChange={(e) => setS({ ...s, autopay_max_attempts: e.target.value })} onBlur={() => save({ autopay_max_attempts: parseInt(s.autopay_max_attempts, 10) || 3 })} data-testid="autopay-max-attempts-input" />
        </div>
      </div>
      <div>
        <Label>Consent text shown to customers (version {s.autopay_consent_version})</Label>
        <textarea value={consent} onChange={(e) => setConsent(e.target.value)} rows={4} className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm" data-testid="autopay-consent-textarea" />
        <p className="text-xs text-slate-500 mt-1">Placeholders: {"{company}"}, {"{timing}"}. Saving a change bumps the version; customers who consented to an older version keep their mandate, new sign-ups see the new text.</p>
        <Button size="sm" className="mt-2 bg-indigo-700 hover:bg-indigo-800" onClick={() => save({ autopay_consent_text: consent })} disabled={!!busy || consent === s.autopay_consent_text} data-testid="autopay-consent-save">Save consent text</Button>
      </div>
    </div>
  );
};
